// \src\pages\outlet\page.tsx

import { useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin, type Product, type Seller } from "@/context/AdminContext";

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isSellerAvailable(seller: Seller): boolean {
  if (seller.available) return true;
  if (!seller.inactiveUntil) return false;
  return seller.inactiveUntil <= getLocalDateString();
}

function isProductAvailable(product: Product): boolean {
  if (!product.active) return false;
  if (!product.activeUntil) return true;
  return product.activeUntil >= getLocalDateString();
}

function renderMedia(
  src: string,
  alt: string,
  className: string,
) {
  if (!src) return null;

  if (isVideoMedia(src)) {
    return (
      <video
        src={src}
        className={className}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={alt}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
    />
  );
}

export default function Outlet() {
  const { data } = useAdmin();
  const outlet = data.outletPage;

  const outletStore = useMemo(
    () => data.stores.find(store => /outlet/i.test(store.name)) || data.stores[1],
    [data.stores],
  );

  const outletProducts = useMemo(
    () => data.outletProducts
      .map(id => data.products.find(product => product.id === id))
      .filter((product): product is Product => Boolean(product))
      .filter(isProductAvailable),
    [data.outletProducts, data.products],
  );

  const outletSellers = useMemo(() => {
    const available = data.sellers
      .filter(seller => seller.store === "filial")
      .filter(isSellerAvailable);

    return [...available].sort(() => Math.random() - 0.5);
  }, [data.sellers]);

  if (!outlet.active) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[70vh] bg-background-50 flex items-center justify-center px-4">
          <div className="max-w-xl text-center py-20">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center">
              <i className="ri-store-3-line text-3xl" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-3">MZ Outlet</h1>
            <p className="text-foreground-600 text-sm mb-6">Esta página está temporariamente indisponível.</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer">
              <i className="ri-home-4-line" /> Voltar ao início
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        <section className="relative min-h-[360px] md:min-h-[460px] overflow-hidden bg-foreground-950">
          {outlet.heroMedia ? (
            renderMedia(
              outlet.heroMedia,
              outlet.title || "MZ Outlet",
              "absolute inset-0 w-full h-full object-cover object-center",
            )
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-foreground-950 via-foreground-900 to-primary-950" />
          )}

          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />

          <div className="relative z-10 max-w-7xl mx-auto min-h-[360px] md:min-h-[460px] px-4 md:px-8 flex items-center">
            <div className="max-w-2xl py-14">
              <nav className="text-xs text-background-300 font-label mb-4">
                <Link to="/" className="hover:text-primary-300 cursor-pointer">Início</Link>
                <span className="mx-2">/</span>
                <span className="text-primary-300">MZ Outlet</span>
              </nav>

              {outlet.subtitle && (
                <span className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-xs font-label font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
                  <i className="ri-store-3-line" />
                  {outlet.subtitle}
                </span>
              )}

              <h1 className="text-4xl md:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
                {outlet.title}
              </h1>

              {outlet.description && (
                <p className="text-background-200 text-base md:text-lg leading-relaxed max-w-xl mb-7">
                  {outlet.description}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                {outletStore?.whatsapp && (
                  <a
                    href={`https://wa.me/${outletStore.whatsapp}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-lg" />
                    Falar com a MZ Outlet
                  </a>
                )}

                <a
                  href="#produtos-outlet"
                  className="inline-flex items-center gap-2 bg-background-50/10 hover:bg-background-50/20 border border-background-50/30 text-background-50 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-3-line" />
                  Ver produtos
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-primary-600 text-xs font-label font-bold uppercase tracking-widest mb-3">MZ Outlet</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              {outlet.introTitle}
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed whitespace-pre-wrap">
              {outlet.introText}
            </p>
          </div>
        </section>

        {(outlet.showcaseTitle || outlet.showcaseText || outlet.showcaseMediaOne || outlet.showcaseMediaTwo) && (
          <section className="bg-background-100 border-y border-background-200 py-14 md:py-16 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center max-w-2xl mx-auto mb-8">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                  {outlet.showcaseTitle}
                </h2>
                {outlet.showcaseText && (
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed whitespace-pre-wrap">
                    {outlet.showcaseText}
                  </p>
                )}
              </div>

              {(outlet.showcaseMediaOne || outlet.showcaseMediaTwo) && (
                <div className={`grid gap-5 ${outlet.showcaseMediaOne && outlet.showcaseMediaTwo ? "md:grid-cols-2" : "grid-cols-1 max-w-4xl mx-auto"}`}>
                  {outlet.showcaseMediaOne && (
                    <div className="rounded-2xl overflow-hidden bg-foreground-950 border border-background-200 min-h-[260px] md:min-h-[360px]">
                      {renderMedia(outlet.showcaseMediaOne, `${outlet.showcaseTitle} 1`, "w-full h-full min-h-[260px] md:min-h-[360px] object-cover")}
                    </div>
                  )}
                  {outlet.showcaseMediaTwo && (
                    <div className="rounded-2xl overflow-hidden bg-foreground-950 border border-background-200 min-h-[260px] md:min-h-[360px]">
                      {renderMedia(outlet.showcaseMediaTwo, `${outlet.showcaseTitle} 2`, "w-full h-full min-h-[260px] md:min-h-[360px] object-cover")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        <section id="produtos-outlet" className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-16 scroll-mt-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {outlet.productsTitle}
            </h2>
            {outlet.productsText && <p className="text-foreground-600 text-sm md:text-base">{outlet.productsText}</p>}
          </div>

          {outletProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {outletProducts.map(product => (
                <Link
                  key={product.id}
                  to={`/produto/${product.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="relative aspect-square bg-background-100 overflow-hidden">
                    {product.images[0] ? (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-image-line text-4xl" /></div>
                    )}
                    {product.promoLabel && (
                      <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-[10px] md:text-xs font-label font-bold px-2 py-1 rounded">
                        {product.promoLabel}
                      </span>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    {product.brand && <p className="text-foreground-400 text-[10px] md:text-xs font-label uppercase tracking-wide mb-1">{product.brand}</p>}
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm md:text-base leading-tight line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
                    {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                    {product.showPrice && (
                      <p className="text-primary-600 font-heading font-bold text-base md:text-lg mt-2">
                        R$ {product.price.toFixed(2).replace(".", ",")}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-background-300 rounded-xl p-10 text-center bg-background-50">
              <i className="ri-shopping-bag-3-line text-4xl text-foreground-300 block mb-3" />
              <p className="text-foreground-600 text-sm">Os produtos da MZ Outlet serão publicados aqui em breve.</p>
            </div>
          )}
        </section>

        <section className="bg-foreground-950 py-14 md:py-16 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                {outlet.sellersTitle}
              </h2>
              {outlet.sellersText && <p className="text-background-400 text-sm md:text-base">{outlet.sellersText}</p>}
            </div>

            {outletSellers.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                {outletSellers.map(seller => (
                  <div key={seller.id} className="bg-background-50 rounded-xl p-4 text-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-3 border-2 border-primary-100 bg-background-100">
                      {seller.photo ? <img src={seller.photo} alt={seller.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-user-line text-3xl" /></div>}
                    </div>
                    <p className="font-label font-semibold text-foreground-950 text-sm leading-tight">{seller.name}</p>
                    <p className="text-foreground-500 text-xs mt-1 mb-3">{seller.role}</p>
                    <a href={`https://wa.me/${seller.whatsapp}`} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-background-50 px-3 py-2 rounded-full text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-whatsapp-line" /> WhatsApp
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-background-400 text-sm text-center">Nenhum vendedor da MZ Outlet está disponível no momento.</p>
            )}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {outlet.contactTitle}
            </h2>
            {outlet.contactText && <p className="text-foreground-600 text-sm md:text-base">{outlet.contactText}</p>}
          </div>

          {outletStore ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-5">
                  <i className="ri-store-3-line text-2xl" />
                </div>
                <h3 className="font-heading font-bold text-foreground-950 text-2xl mb-4">{outletStore.name}</h3>
                <div className="space-y-3 text-sm text-foreground-600">
                  <p className="flex items-start gap-3"><i className="ri-map-pin-2-line text-primary-500 mt-0.5" /><span>{outletStore.address}{outletStore.city ? ` — ${outletStore.city}` : ""}{outletStore.cep ? ` — CEP ${outletStore.cep}` : ""}</span></p>
                  {outletStore.phone && <p className="flex items-center gap-3"><i className="ri-phone-line text-primary-500" /><a href={`tel:${outletStore.phone.replace(/\D/g, "")}`} className="hover:text-primary-600">{outletStore.phone}</a></p>}
                  {outletStore.hours && <p className="flex items-start gap-3"><i className="ri-time-line text-primary-500 mt-0.5" /><span>{outletStore.hours}</span></p>}
                </div>
                <div className="flex flex-wrap gap-2 mt-6">
                  {outletStore.whatsapp && <a href={`https://wa.me/${outletStore.whatsapp}`} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-background-50 px-5 py-2.5 rounded-md text-sm font-label font-semibold transition-all cursor-pointer"><i className="ri-whatsapp-line" /> WhatsApp</a>}
                  {outletStore.googleMapsUrl && <a href={outletStore.googleMapsUrl} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-md text-sm font-label font-semibold transition-all cursor-pointer"><i className="ri-google-line" /> Google Maps</a>}
                  {outletStore.wazeUrl && <a href={outletStore.wazeUrl} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 text-foreground-700 px-5 py-2.5 rounded-md text-sm font-label font-semibold transition-all cursor-pointer"><i className="ri-navigation-line" /> Waze</a>}
                </div>
              </div>

              <div className="min-h-[340px] rounded-xl overflow-hidden border border-background-200 bg-background-100">
                {outletStore.mapUrl ? (
                  <iframe
                    src={outletStore.mapUrl}
                    title={`Mapa ${outletStore.name}`}
                    className="w-full h-full min-h-[340px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full min-h-[340px] flex items-center justify-center text-foreground-400">
                    <div className="text-center"><i className="ri-map-2-line text-4xl block mb-2" /><p className="text-sm">Mapa ainda não cadastrado.</p></div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-background-300 rounded-xl p-10 text-center text-foreground-600 text-sm">
              O cadastro da MZ Outlet ainda não foi localizado em Lojas.
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
